import { useState, useCallback, useMemo } from 'react';
import { ModuleShell } from '../components/ModuleShell';
import { StatusChip } from '../components/StatusChip';
import { api } from '../api/client';
import { timeAgo, sevVariant, sevColor } from '../api/format';
import type { Alert, AlertsResponse, ModuleState } from '../api/types';
import type { Filters } from '../state/useURLState';

const SEV_ORDER: Record<string, number> = { red: 0, amber: 1, info: 2 };

function parseTriggerContext(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function AlertCard({ alert, onAction, onClick, selected, onToggleSelect }: {
  alert: Alert; onAction: (id: string, action: string) => Promise<void>;
  onClick?: () => void; selected?: boolean; onToggleSelect?: () => void;
}) {
  const [acting, setActing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const color = sevColor(alert.severity);
  const ctx = parseTriggerContext(alert.trigger_context);

  const handleAction = async (action: string) => {
    if (action === 'mute' && !window.confirm(`Mute alert "${alert.title}"?`)) return;
    setActing(action); setActionError(null);
    try { await onAction(alert.id, action); }
    catch (e) { setActionError(e instanceof Error ? e.message : 'Failed'); }
    finally { setActing(null); }
  };

  return (
    <div style={{ padding: '14px 16px', background: selected ? 'var(--blue-bg)' : 'var(--bg-surface)', borderLeft: `3px solid ${color}`, borderRadius: '0 var(--radius-md) var(--radius-md) 0', marginBottom: 8, border: selected ? '1px solid var(--blue-border)' : undefined }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 8, flex: 1 }}>
          {onToggleSelect && (
            <input type="checkbox" checked={!!selected} onChange={onToggleSelect}
              style={{ marginTop: 2, cursor: 'pointer', flexShrink: 0 }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{alert.client_id}</span>
              <StatusChip label={alert.severity} variant={sevVariant(alert.severity)} showIcon />
              {alert.state !== 'open' && <StatusChip label={alert.state} variant="muted" />}
              {alert.muted_until && <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>until {alert.muted_until}</span>}
              {alert.owner && <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>→ {alert.owner}</span>}
            </div>
            <div onClick={onClick} style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginTop: 4, cursor: onClick ? 'pointer' : 'default' }}>
              {alert.title} {onClick && <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>↗</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 2 }}>{alert.detail}</div>
          </div>
        </div>
        <span style={{ fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap', marginLeft: 12 }}>{timeAgo(alert.first_fired_at)}</span>
      </div>
      {ctx && Object.keys(ctx).length > 0 && (
        <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--bg-root)', borderRadius: 'var(--radius-sm)', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-dim)' }}>
          {Object.entries(ctx).map(([k, v]) => (
            <div key={k}><span style={{ color: 'var(--text-ghost)' }}>{k}:</span> <span style={{ color: 'var(--text-secondary)' }}>{String(v)}</span></div>
          ))}
        </div>
      )}
      {alert.state === 'open' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          {['acknowledge', 'resolve', 'mute'].map(action => (
            <button key={action} onClick={() => handleAction(action)} disabled={acting !== null}
              style={{ padding: '3px 10px', fontSize: 10, fontWeight: 600, background: action === 'resolve' ? 'var(--green-bg)' : 'var(--bg-active)', border: `1px solid ${action === 'resolve' ? 'var(--green-border)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-sm)', color: action === 'resolve' ? 'var(--green)' : 'var(--text-secondary)', cursor: acting ? 'wait' : 'pointer', opacity: acting && acting !== action ? 0.5 : 1 }}>
              {acting === action ? '...' : action}
            </button>
          ))}
          {actionError && <span style={{ fontSize: 10, color: 'var(--red)' }}>{actionError}</span>}
        </div>
      )}
    </div>
  );
}

interface Props { moduleState: ModuleState<AlertsResponse> & { refetch: () => void }; filters: Filters; onAlertClick?: (alert: Alert) => void }

export function AlertsModule({ moduleState, filters, onAlertClick }: Props) {
  const all = moduleState.data?.alerts || [];
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActing, setBulkActing] = useState(false);
  const [lastBulkAction, setLastBulkAction] = useState<{ action: string; ids: string[] } | null>(null);

  const filtered = useMemo(() => {
    let r = [...all];
    if (filters.client) r = r.filter(a => a.client_id === filters.client);
    if (filters.severity) r = r.filter(a => a.severity === filters.severity);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!filters.searchScope || filters.searchScope === 'alerts') {
        r = r.filter(a => a.client_id.includes(q) || a.title.toLowerCase().includes(q) || a.detail.toLowerCase().includes(q) || a.type.includes(q));
      }
    }
    return r.sort((a, b) => {
      if (a.state === 'open' && b.state !== 'open') return -1;
      if (a.state !== 'open' && b.state === 'open') return 1;
      return (SEV_ORDER[a.severity] ?? 99) - (SEV_ORDER[b.severity] ?? 99) || new Date(b.first_fired_at).getTime() - new Date(a.first_fired_at).getTime();
    });
  }, [all, filters]);

  const openCount = filtered.filter(a => a.state === 'open').length;

  const handleAction = useCallback(async (id: string, action: string) => {
    await api.alertAction(id, action);
    setTimeout(() => moduleState.refetch(), 500);
  }, [moduleState]);

  // Bulk action
  const handleBulkAction = useCallback(async (action: string) => {
    if (selectedIds.size === 0) return;
    if (action === 'mute' && !window.confirm(`Mute ${selectedIds.size} alerts?`)) return;
    setBulkActing(true);
    const ids = [...selectedIds];
    try {
      await Promise.allSettled(ids.map(id => api.alertAction(id, action)));
      setLastBulkAction({ action, ids });
      setSelectedIds(new Set());
      setTimeout(() => moduleState.refetch(), 500);
    } finally { setBulkActing(false); }
  }, [selectedIds, moduleState]);

  // Undo (best effort — re-applies opposite action)
  const handleUndo = useCallback(async () => {
    if (!lastBulkAction) return;
    // Undo resolve → reopen not supported by backend, but we can try acknowledge
    // This is best-effort — backend may not support true undo
    const undoAction = lastBulkAction.action === 'mute' ? 'acknowledge' : 'acknowledge';
    await Promise.allSettled(lastBulkAction.ids.map(id => api.alertAction(id, undoAction)));
    setLastBulkAction(null);
    setTimeout(() => moduleState.refetch(), 500);
  }, [lastBulkAction, moduleState]);

  return (
    <ModuleShell name="alerts" title="Alerts" status={moduleState.status} error={moduleState.error} dataFreshness={moduleState.dataFreshness} fetchedAt={moduleState.fetchedAt} isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason} onRetry={moduleState.refetch}
      headerRight={<StatusChip label={`${openCount} open`} variant={openCount > 0 ? 'warning' : 'success'} showIcon />}>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px', marginBottom: 12, background: 'var(--blue-bg)', border: '1px solid var(--blue-border)', borderRadius: 'var(--radius-md)', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue)' }}>{selectedIds.size} selected</span>
          {['acknowledge', 'resolve', 'mute'].map(action => (
            <button key={action} onClick={() => handleBulkAction(action)} disabled={bulkActing}
              style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, background: action === 'resolve' ? 'var(--green-bg)' : 'var(--bg-active)', border: `1px solid ${action === 'resolve' ? 'var(--green-border)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-sm)', color: action === 'resolve' ? 'var(--green)' : 'var(--text-secondary)', cursor: bulkActing ? 'wait' : 'pointer' }}>
              {bulkActing ? '...' : `Bulk ${action}`}
            </button>
          ))}
          <button onClick={() => setSelectedIds(new Set())} style={{ padding: '4px 12px', fontSize: 11, background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', color: 'var(--text-ghost)', cursor: 'pointer' }}>Cancel</button>
        </div>
      )}

      {/* Undo bar */}
      {lastBulkAction && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 16px', marginBottom: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {lastBulkAction.action}d {lastBulkAction.ids.length} alert{lastBulkAction.ids.length !== 1 ? 's' : ''}
          </span>
          <button onClick={handleUndo} style={{ padding: '3px 10px', fontSize: 11, fontWeight: 600, background: 'var(--yellow-bg)', border: '1px solid var(--yellow-border)', borderRadius: 'var(--radius-sm)', color: 'var(--yellow)', cursor: 'pointer' }}>
            Undo
          </button>
        </div>
      )}

      {filtered.length === 0
        ? <div style={{ padding: 48, textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)' }}><div style={{ fontSize: 24, color: 'var(--green)' }}>✓</div><div style={{ fontSize: 13, color: 'var(--text-ghost)', marginTop: 8 }}>No open alerts</div></div>
        : filtered.map(a => (
          <AlertCard key={a.id} alert={a} onAction={handleAction}
            onClick={onAlertClick ? () => onAlertClick(a) : undefined}
            selected={selectedIds.has(a.id)}
            onToggleSelect={() => setSelectedIds(prev => { const n = new Set(prev); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); return n; })} />
        ))
      }
    </ModuleShell>
  );
}
